import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ApiResponse, RawPluginApi } from "@fuse/api/pluginDataApi"
import PluginRenderer from "@fuse/sharedComponents/PluginRenderer"
import { useRecoilValue } from "recoil"

import { useTestData } from "../../../recoil/pluginStudio"
import { activeFilePreviewSelector } from "../../../recoil/pluginStudio/selectors"
import {
  canViewAs,
  ErrorSeverity,
  EventData,
  getEffectiveViewRole,
  hasTranspiledContent,
  PluginErrorType,
  PreviewPanelProps,
  PreviewPluginData,
  PreviewStudioState,
  UserRole,
  validateEventData,
} from "../../../recoil/pluginStudio/types"
import {
  devicePresetConfigs,
  DeviceSelector,
  DeviceType,
} from "./DeviceSelector"
import ErrorDisplay from "./ErrorDisplay"

/**
 * Presentation component for the preview panel
 */
export const PreviewPanelUI: React.FC<PreviewPanelProps> = ({
  isStale = false,
  viewType = UserRole.GUEST,
  transpiledCode = "",
  onRefreshPreview,
  lastAutoUpdate = 0,
  studioState,
  studioApi,
  previewPlugin,
  eventData,
  activeLiveUser,
  eventDataErrors = [],
  userCanViewAsCurrentType = true,
}) => {
  const [internalLoading, setInternalLoading] = useState(false)
  const [lastUpdateTime, setLastUpdateTime] = useState<string>("")
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>(
    DeviceType.DESKTOP,
  )

  // Update the last update time when lastAutoUpdate changes
  useEffect(() => {
    if (lastAutoUpdate > 0) {
      const now = new Date()
      const timeString = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      setLastUpdateTime(timeString)
    }
  }, [lastAutoUpdate])

  // Track if this is the first render to avoid multiple initializations
  const initializedRef = useRef(false)

  // Comment out effect that calls onRefreshPreview
  /*
  // Effect to mark rendered content as no longer stale
  useEffect(() => {
    if (isStale && onRefreshPreview) {
      // Small delay to ensure rendering has happened
      const timer = setTimeout(() => {
        onRefreshPreview()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isStale, onRefreshPreview])
  */

  return (
    <div className="flex flex-col h-full">
      <div className="h-10 bg-gray-800 flex items-center px-4 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <svg
            className="h-4 w-4 text-blue-400"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zm-7 5h5v5h-5v-5z" />
          </svg>
          <span className="font-medium">{eventData?.title || "Event"}</span>

          <span className="text-gray-400 mx-1">|</span>

          <span className="flex items-center">
            {viewType === UserRole.HOST_CONFIG_VIEW ? (
              <svg
                className="h-4 w-4 text-purple-400 mr-1"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7z" />
              </svg>
            ) : viewType === UserRole.HOST_EVENT_VIEW ? (
              <svg
                className="h-4 w-4 text-blue-400 mr-1"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-11h2v6h-2zm0-4h2v2h-2z" />
              </svg>
            ) : (
              <svg
                className="h-4 w-4 text-green-400 mr-1"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m0 10c2.7 0 5.8 1.29 6 2H6c.23-.72 3.31-2 6-2m0-12C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
            <span className="font-medium">
              {activeLiveUser?.userData?.displayName || "User"}
            </span>
            <span className="text-xs ml-1 text-gray-400">({viewType})</span>
          </span>

          <span className="text-gray-400 mx-1">|</span>

          <DeviceSelector
            selectedDeviceType={selectedDeviceType}
            onDeviceSelect={setSelectedDeviceType}
          />
        </div>

        <div className="flex items-center ml-auto">
          {internalLoading && (
            <span className="text-blue-400 text-sm flex items-center mr-3">
              <div className="mr-2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              Loading...
            </span>
          )}

          {isStale && (
            <span className="text-yellow-400 text-sm flex items-center bg-yellow-900/30 px-2 py-1 rounded mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Changes pending
            </span>
          )}

          {lastUpdateTime && (
            <span className="text-gray-400 text-xs px-2 py-1 mr-3">
              Last updated: {lastUpdateTime}
            </span>
          )}

          {/* Studio mode indicator */}
          <span className="text-green-400 text-sm bg-green-900/30 px-2 py-1 rounded flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z"
                clipRule="evenodd"
              />
            </svg>
            Studio Mode
          </span>
        </div>
      </div>

      <div className="h-[calc(100%-2.5rem)] overflow-auto p-4">
        <div
          className={`h-full transition-all duration-300 overflow-y-auto overflow-x-hidden bg-gray-900`}
          style={{
            width: devicePresetConfigs[selectedDeviceType].width,
            height: devicePresetConfigs[selectedDeviceType].height,
            maxHeight: "100%",
            margin: selectedDeviceType === DeviceType.DESKTOP ? "0" : "auto",
          }}
        >
          {internalLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
                <p className="text-gray-400">Loading plugin preview...</p>
              </div>
            </div>
          ) : transpiledCode ? (
            <div className="flex items-center justify-center h-full">
              {/* Display validation errors if they exist */}
              {eventDataErrors && eventDataErrors.length > 0 && (
                <ErrorDisplay
                  errors={eventDataErrors}
                  title="Event Data Validation Errors"
                  className="absolute top-4 right-4 max-w-md z-10"
                />
              )}

              {!userCanViewAsCurrentType && (
                <ErrorDisplay
                  errors={[
                    {
                      type: PluginErrorType.PERMISSION,
                      message: `${activeLiveUser?.role} role cannot view as ${viewType}`,
                      severity: ErrorSeverity.WARNING,
                      timestamp: Date.now(),
                    },
                  ]}
                  title="Role Mismatch Warning"
                  className="absolute top-4 left-4 max-w-md z-10"
                />
              )}

              <PluginRenderer
                key={`plugin-preview-${viewType}-${transpiledCode.length}-${lastAutoUpdate}`}
                plugin={previewPlugin}
                studioApi={studioApi}
                config={studioState.getPluginConfig ? {} : {}}
                eventId={studioState.eventId || "preview-event-id"}
                userId={studioState.userId || "preview-user-id"}
                userRole={viewType as UserRole}
                eventMetadata={{
                  name: eventData?.title || "Event Preview",
                  description: eventData?.description || "Event Description",
                  timing: {
                    eventStartTime:
                      (typeof eventData?.startTime === "string"
                        ? eventData.startTime
                        : eventData?.startTime?.toISOString()) ||
                      new Date().toISOString(),
                    eventEndTime:
                      (typeof eventData?.endTime === "string"
                        ? eventData.endTime
                        : eventData?.endTime?.toISOString()) ||
                      new Date(Date.now() + 86400000).toISOString(),
                  },
                  location: {
                    eventLocation:
                      eventData?.location?.address || "Event Location",
                  },
                }}
                isLoggedIn={true}
                isStudioMode={true}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-gray-500 mx-auto mb-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <h3 className="text-gray-300 text-lg font-semibold mb-2">
                  No compatible code available for this plugin
                </h3>
                <p className="text-gray-500">
                  There might be a transpilation error, or no code has been
                  written yet for the selected view type. Check the console for
                  any error messages.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const PreviewPanel: React.FC = () => {
  // Comment out refreshPreview from the hook to prevent re-renders
  // const { refreshPreview } = usePlugins()
  // Use a no-op function instead
  const refreshPreview = React.useCallback(() => {
    // No-op function to prevent unnecessary state updates
    console.log("Preview refresh disabled")
  }, [])

  // Use a constant false value instead of the reactive state
  const isPreviewStale = false // Hardcoded to prevent re-renders

  const activeFilePreview = useRecoilValue(activeFilePreviewSelector)
  const {
    liveData,
    activeLiveUser,
    updateLiveEventData,
    updateLivePluginConfig,
    setLiveActiveUser,
  } = useTestData()
  const [lastAutoUpdate, setLastAutoUpdate] = useState<number>(0)

  // Initialize localStorage for plugin data
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true
      // Check if there's cached data in localStorage and use that if available
      try {
        const cachedEventPluginData = localStorage.getItem(
          "preview_eventPluginData",
        )
        if (cachedEventPluginData) {
          const parsedData = JSON.parse(cachedEventPluginData)
          // Update through Recoil directly
          updateLiveEventData(parsedData)
        }
      } catch (error) {
        console.error("Error loading cached plugin data:", error)
      }
    }
  }, [updateLiveEventData, liveData.eventData])

  // Save plugin data to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "preview_eventPluginData",
        JSON.stringify(liveData.eventData),
      )
    } catch (error) {
      console.error("Error saving plugin data to cache:", error)
    }
  }, [liveData.eventData])

  // Comment out this effect that watches isPreviewStale
  /*
  // When the preview is refreshed, update the lastAutoUpdate timestamp
  useEffect(() => {
    if (!isPreviewStale) {
      setLastAutoUpdate(Date.now())
    }
  }, [isPreviewStale])
  */

  // Determine active view type from the file
  const viewType = useMemo(() => {
    // Get type from UserRole enum
    if (activeFilePreview?.type === UserRole.GUEST) return UserRole.GUEST
    if (activeFilePreview?.type === UserRole.HOST_EVENT_VIEW)
      return UserRole.HOST_EVENT_VIEW
    if (activeFilePreview?.type === UserRole.HOST_CONFIG_VIEW)
      return UserRole.HOST_CONFIG_VIEW
    return UserRole.GUEST // Default to guest view
  }, [activeFilePreview?.type])

  // Use the helper function to check if we have valid transpiled content
  const hasValidTranspiledContent = useMemo(() => {
    return (
      activeFilePreview?.transpiledContent &&
      activeFilePreview.transpiledContent.trim() !== ""
    )
  }, [activeFilePreview?.transpiledContent])

  // Get the effective view role for the active user
  const effectiveUserRole = useMemo(() => {
    return getEffectiveViewRole(activeLiveUser) || UserRole.GUEST
  }, [activeLiveUser])

  // Validate event data to ensure it's properly formatted
  const eventDataErrors = useMemo(() => {
    return validateEventData(liveData.eventData)
  }, [liveData.eventData])

  // Check if the active user can view as the current viewType
  const userCanViewAsCurrentType = useMemo(() => {
    return canViewAs(activeLiveUser, viewType)
  }, [activeLiveUser, viewType])

  // Create the studioState object for the plugin renderer
  const studioState = useMemo<PreviewStudioState>(
    () => ({
      plugins: [],
      liveData,
      eventId: liveData.sourceTestCaseId,
      userId: activeLiveUser?.id,
      getPluginConfig: async () => {
        return liveData.pluginConfig || {}
      },
      savePluginConfig: async (newConfig: any) => {
        updateLivePluginConfig({
          pluginConfig: newConfig,
        })
        return Promise.resolve()
      },
      getPluginEventInstanceData: async () => {
        return liveData.eventData || {}
      },
      savePluginEventInstanceData: async (data: any) => {
        // Update event data directly through Recoil
        updateLiveEventData(data)
        return Promise.resolve()
      },
      getEventPluginUserData: async () => {
        return activeLiveUser?.userData || {}
      },
      saveEventPluginUserData: async (data: any, userId?: string) => {
        console.log(
          "saveEventPluginUserData called with data",
          data,
          "and userId",
          userId,
        )
        const targetUserId = userId || activeLiveUser?.id

        if (targetUserId) {
          // Update user data directly through Recoil
          setLiveActiveUser(targetUserId)
        } else {
          console.log("No user ID provided, skipping update")
        }
        return Promise.resolve()
      },
    }),
    [
      liveData,
      activeLiveUser,
      updateLiveEventData,
      updateLivePluginConfig,
      setLiveActiveUser,
    ],
  )

  // Create a RawPluginApi object from the studioState
  const studioApi = useMemo<RawPluginApi>(
    () => ({
      getPluginConfig: async (
        eventId: string,
        pluginId: string,
      ): Promise<ApiResponse<any>> => {
        try {
          const data = (await studioState.getPluginConfig?.()) || {}
          return { success: true, data }
        } catch (error) {
          return { success: false, data: {}, error: String(error) }
        }
      },
      savePluginConfig: async (
        eventId: string,
        pluginId: string,
        config: any,
      ): Promise<ApiResponse<void>> => {
        try {
          await studioState.savePluginConfig?.(config)
          return { success: true, data: undefined }
        } catch (error) {
          return { success: false, data: undefined, error: String(error) }
        }
      },
      getPluginEventInstanceData: async (
        eventId: string,
        pluginId: string,
      ): Promise<ApiResponse<any>> => {
        try {
          const data = (await studioState.getPluginEventInstanceData?.()) || {}
          return { success: true, data }
        } catch (error) {
          return { success: false, data: {}, error: String(error) }
        }
      },
      savePluginEventInstanceData: async (
        eventId: string,
        pluginId: string,
        data: any,
      ): Promise<ApiResponse<void>> => {
        try {
          await studioState.savePluginEventInstanceData?.(data)
          return { success: true, data: undefined }
        } catch (error) {
          return { success: false, data: undefined, error: String(error) }
        }
      },
      getEventPluginUserData: async (
        eventId: string,
        pluginId: string,
        userId: string,
      ): Promise<ApiResponse<any>> => {
        try {
          const data = (await studioState.getEventPluginUserData?.()) || {}
          return { success: true, data }
        } catch (error) {
          return { success: false, data: {}, error: String(error) }
        }
      },
      saveEventPluginUserData: async (
        eventId: string,
        pluginId: string,
        userId: string,
        data: any,
      ): Promise<ApiResponse<void>> => {
        try {
          await studioState.saveEventPluginUserData?.(data, userId)
          return { success: true, data: undefined }
        } catch (error) {
          return { success: false, data: undefined, error: String(error) }
        }
      },
    }),
    [studioState],
  )

  // Create a preview plugin object with the transpiled code
  const previewPlugin: PreviewPluginData = {
    id: "preview-plugin-id",
    name: "Preview Plugin",
    files: [],
    manifest: {
      name: "Preview Plugin",
      description: "Plugin Preview",
      version: "1.0.0",
      author: "Let's Hang",
    },
    code: {
      frontendGuestCodeWeb: "",
      frontendHostViewCodeWeb: "",
      frontendHostConfigCodeWeb: "",
      transpiledFrontendGuestCodeWeb:
        viewType === UserRole.GUEST
          ? activeFilePreview?.transpiledContent || ""
          : "",
      transpiledFrontendHostViewCodeWeb:
        viewType === UserRole.HOST_EVENT_VIEW
          ? activeFilePreview?.transpiledContent || ""
          : "",
      transpiledFrontendHostConfigCodeWeb:
        viewType === UserRole.HOST_CONFIG_VIEW
          ? activeFilePreview?.transpiledContent || ""
          : "",
    },
  }

  return (
    <PreviewPanelUI
      viewType={viewType}
      transpiledCode={activeFilePreview?.transpiledContent || ""}
      isStale={isPreviewStale} // Always false now
      onRefreshPreview={refreshPreview}
      lastAutoUpdate={lastAutoUpdate}
      studioState={studioState}
      studioApi={studioApi}
      previewPlugin={previewPlugin}
      eventData={liveData.eventData}
      activeLiveUser={activeLiveUser}
      eventDataErrors={eventDataErrors}
      userCanViewAsCurrentType={userCanViewAsCurrentType}
    />
  )
}

export default PreviewPanel
